import { defaultTrackingBaseUrl } from '../web/pulse/constants/claims';
import { Claims } from '../types/database';
import { claimStateMapper } from './claims-orders.helper';

/*
  same functionality in ~/apps/valet/helpers/claims.helper.ts

  TODO: merge in the future
*/

/*
  Claim is considered in the item level if:
  * claimType is `MULTIPLE`
  * claim has multiple values for `claimItem.claimType`
*/

export function isClaimItemLevel(claim: Claims.Claim) {
  if (String(claim.claimType).toUpperCase() === Claims.ClaimType.MULTIPLE) {
    return true;
  }

  const uniqueClaimTypes = new Set(claim?.claimItems?.map(item => item.claimType));
  return uniqueClaimTypes.size > 1;
}

export function isClaimOrderLevel(claim: Claims.Claim) {
  return !isClaimItemLevel(claim);
}

export const buildTrackingLink = ({
  trackingUrl,
  trackingNumber,
}: {
  trackingUrl?: string;
  trackingNumber?: string;
}) => {
  return trackingUrl ? trackingUrl : defaultTrackingBaseUrl + `/${trackingNumber}`;
};

export const formatClaimType = (claimType?: Claims.ClaimState) => {
  // PR FLAG: can anyone tell me why this was maybe optional?
  // it seems like it should be required and we'd never pass a ClaimState that doesn't exist

  if (!claimType) {
    // I'd like to remove this if we always call this function with a value.
    return '';
  }

  return claimType.replace(/_/g, ' ').toLowerCase();
};

function assertClaimState(claimState: string): asserts claimState is Claims.ClaimState {
  if (!Claims.ClaimState[claimState]) {
    throw Error('not a valid claim state');
  }
}

export const formatClaimState = (claimState: string) => {
  assertClaimState(claimState);

  const state = formatClaimType(claimState);
  return claimStateMapper.label(state);
};

export const ClaimSessionStatusToText: { [key: string]: string } = {
  ISSUES: '/issues',
  PROCESS: '/process',
  ISSUES_ITEMS: '/issues/items',
  ISSUES_SHIPMENT: '/issues/shipment',
  ISSUES_SHIPMENT_DETAILS: '/issues/shipment/details',
  RESOLUTION: '/resolution',
  RESOLUTION_REVIEW: '/resolution/review',
  MULTIPLE_SHIPMENTS: '/multiple-shipments',
};

/**
 * Calculate total claimed quantity for a specific fulfillment item
 *
 * @param claims Array of claims
 * @param sourceItemId Source item ID to match against
 * @returns Total claimed quantity for the item
 */
export function calculateTotalClaimedQuantity(
  claims: Array<{
    claimItems: Array<{
      sourceItemId: string;
      quantity: number;
    }>;
  }>,
  sourceItemId: string,
): number {
  const claimItems = claims
    .flatMap(claim => claim.claimItems)
    .filter(claimItem => claimItem.sourceItemId === sourceItemId);

  return claimItems.reduce((total: number, claimItem) => total + claimItem.quantity, 0);
}

/**
 * Check if a fulfillment item can have more claims filed based on its current claimed quantity
 *
 * @param claims Array of claims
 * @param sourceItemId Source item ID to match against
 * @param fulfillmentQuantity Total quantity in the fulfillment
 * @returns Whether more claims can be filed for this item
 */
export function checkFulfillmentItemCanFileClaim(
  claims: Array<{
    claimItems: Array<{
      sourceItemId: string;
      quantity: number;
    }>;
  }>,
  sourceItemId: string,
  fulfillmentQuantity: number,
): boolean {
  const claimItems = claims
    .flatMap(claim => claim.claimItems)
    .filter(claimItem => claimItem.sourceItemId === sourceItemId);

  const totalClaimedQuantity = claimItems.reduce((total: number, claimItem) => total + claimItem.quantity, 0);

  return totalClaimedQuantity < fulfillmentQuantity;
}

/**
 * Check fulfillment can at least file claim on one item
 *
 * @param fulfillment
 * @param claims
 * @returns
 */
export function checkFulfillmentCanFileClaim(
  fulfillment: {
    items: Array<{
      item: {
        sourceItemId: string;
      };
      quantity: number;
    }>;
  },
  claims: Array<{
    claimItems: Array<{
      sourceItemId: string;
      quantity: number;
    }>;
  }>,
): boolean {
  const canAtLeastClaimOnOneFulfillmentItem = fulfillment.items.some(fulfillmentItem =>
    checkFulfillmentItemCanFileClaim(claims, fulfillmentItem.item.sourceItemId, fulfillmentItem.quantity),
  );

  return canAtLeastClaimOnOneFulfillmentItem;
}

export function isInStoreWarrantyClaim(claim: Claims.Claim) {
  if (!claim.claimCategory || !claim.salesChannel) return false;
  return claim.claimCategory === Claims.ClaimCategory.WARRANTY && claim.salesChannel === Claims.SalesChannel.IN_STORE;
}

/**
 * Extracts reshipment number from resolution details string
 *
 * Looks for a value enclosed in square brackets, e.g., [ORD-12345]
 *
 * @param details - The resolution details string to parse
 * @returns The reshipment number or null if not found
 *
 * @example
 * extractReshipmentNumber("RESHIP [ORD-12345] claim123 @ January 15, 2025")
 * // Returns: "ORD-12345"
 */
export function extractReshipmentNumber(details: string | null | undefined): string | null {
  if (!details || typeof details !== 'string') {
    return null;
  }

  const match = details.match(/\[([^\]]+)\]/);
  return match ? match[1] : null;
}

/**
 * Extracts resolved date from resolution details string
 *
 * Supports two formats:
 * 1. Date after @ symbol: "... @ January 15, 2025"
 * 2. Date in brackets on new line: "\n[January 15, 2025] - ..."
 *
 * @param details - The resolution details string to parse
 * @returns The resolved date or null if not found
 *
 * @example
 * extractResolvedDate("RESHIP [ORD-12345] claim123 @ January 15, 2025")
 * // Returns: "January 15, 2025"
 *
 * @example
 * extractResolvedDate("REFUND Resolution claim456\n[January 20, 2025] - Processed externally")
 * // Returns: "January 20, 2025"
 */
export function extractResolvedDate(details: string | null | undefined): string | null {
  if (!details || typeof details !== 'string') {
    return null;
  }

  // Try to match date after @ symbol: "... @ date" (up to newline or end of string)
  const dateMatch = details.match(/@\s*([^\n]+)/);
  if (dateMatch) {
    return dateMatch[1].trim();
  }

  // Try alternative format with date in brackets on new line
  if (details.includes('\n')) {
    const lines = details.split('\n');
    const dateLine = lines.find(line => line.trim().startsWith('['));
    if (dateLine) {
      const altDateMatch = dateLine.match(/\[([^\]]+)\]/);
      if (altDateMatch) {
        return altDateMatch[1];
      }
    }
  }

  return null;
}
